/**
 * api/queries.ts — TanStack Query query factories for the 10 endpoint groups.
 *
 * Phase B ships a complete mapping of every Hono route the SPA consumes:
 *   - health      (no auth)
 *   - auth        (me, static-signin, exchange, logout)
 *   - games       (list, detail, access, related)
 *   - classes     (list, create, get, patch, delete)
 *   - invites     (create, list, revoke, redeem)
 *   - members     (list, delete)
 *   - access      (per-class game access list/set/bulk)
 *   - progress    (events, me, export)
 *   - admin       (tutors pending/approve/reject, audit, sync status/runs/trigger)
 *   - preferences (get, put)
 *
 * Each factory returns a typed `queryOptions` or a `MutationOptions` pair
 * suitable for `useQuery`/`useMutation` at the call site. All factories use
 * the `fetchApi` thin wrapper from `./client.js` so the editor sees proper
 * response types, not the `unknown` that `hc<AppType>` collapses to.
 */

import { queryOptions } from '@tanstack/react-query';
import { fetchApi, type ApiResponse } from './client.js';

// ── Health ───────────────────────────────────────────────────────────

export const healthQuery = queryOptions({
  queryKey: ['health'] as const,
  queryFn: async () =>
    fetchApi<{
      status: 'healthy' | string;
      env: string;
    }>('/api/health'),
  staleTime: 30_000,
});

// ── Auth ─────────────────────────────────────────────────────────────

export interface SessionPrincipal {
  userId: string;
  email: string;
  name: string;
  role: 'admin' | 'tutor' | 'player' | 'parent';
  kind: 'admin' | 'tutor' | 'student' | 'player' | 'parent' | 'visitor';
  isApprovedTutor: boolean;
}

export interface ClassMembership {
  classId: string;
  className: string;
  sdGrade: number;
  scheduleText: string | null;
  zoomUrl?: string | null;
  scratchJrAppUrl?: string | null;
  joinedAt: string;
}

export interface MeResponse {
  user: SessionPrincipal;
  memberships: ClassMembership[];
}

export const sessionQuery = queryOptions({
  queryKey: ['session'] as const,
  queryFn: () => fetchApi<MeResponse>('/api/auth/me'),
  // Session is short-lived; refetch whenever we navigate back to the app.
  staleTime: 60_000,
  retry: false,
});

export type SessionQueryResult = Awaited<ReturnType<NonNullable<typeof sessionQuery.queryFn>>>;

/**
 * Force a fresh session fetch. Use inside `beforeLoad` guards so the
 * redirect decision uses the latest server state rather than the
 * possibly-stale cache. The root loader's `prefetchQuery` runs in
 * parallel but `beforeLoad` is invoked first; without this, guards see
 * `getQueryData === undefined` on the very first navigation and bounce
 * to /login even when the cookie is valid.
 */
export const freshSessionQuery = {
  queryKey: sessionQuery.queryKey,
  queryFn: sessionQuery.queryFn!,
  staleTime: 0,
  gcTime: 5 * 60_000,
  retry: false,
} as const;

// ── Games ────────────────────────────────────────────────────────────

export type GameDisplayStatus =
  'public-active' | 'in-progress' | 'classroom-active' | 'public-future';

export interface GameSummary {
  gameId: string;
  slug: string;
  title: string;
  sdGrade: number;
  levelCount: number;
  visibility: 'public' | 'classroom';
  status: 'ready' | 'in-progress' | 'planned';
  displayStatus: GameDisplayStatus;
  relatedGameIds: string[];
  shortDescription?: string;
  thumbnailUrl?: string;
  /** Hydrated by catalogStateQuery. Defaults to true when missing. */
  isEnabled?: boolean;
  /**
   * Konsep coding utama yang diajarkan game ini (e.g. "Algoritma & Urutan",
   * "If-Then (Kondisi)"). Ditampilkan sebagai chip di kartu katalog.
   * v0.2.0-catalog-10 — ada untuk semua game ready (public + classroom).
   */
  concept?: string;
  /** Sub-topik coding (e.g. ["sequence", "ordering"]). */
  conceptTags?: string[];
  /** Label tingkat kesulitan (e.g. "Level 1", "Level 2", "Level 3") untuk display. */
  gradeLabel?: string;
  /** Penjelasan game 1-2 kalimat, ditonjolkan di kartu publik. */
  longDescription?: string;
  /** Cara bermain (1 kalimat). */
  mechanics?: string;
  /** Apa yang akan dipelajari anak dari game ini. */
  whatYouLearn?: string;
  /**
   * Folder sumber di coding-games/packages/games/ untuk `copy.mjs`.
   * Slug (URL) bisa berbeda (mis. g02 → srcFolder g02-pjs karena
   * v0.2.0 mengkonsolidasikan PixiJS redesign ke g02).
   */
  srcFolder?: string;
}

export interface GameListResponse {
  games: GameSummary[];
  total: number;
  publicGames: number;
  message?: string;
}

export const gamesQuery = queryOptions({
  queryKey: ['games'] as const,
  queryFn: () => fetchApi<GameListResponse>('/api/games'),
  staleTime: 60_000,
});

/**
 * Public catalog query — fetches the static `/registry.json` directly
 * (no API roundtrip). The catalogued shape there matches the Worker's
 * `registry` package and is the source of truth that the SPA shows for
 * visitors. The `/api/games` route mirrors the same payload once Fase 4
 * wires a registry sync orchestrator.
 *
 * Fase 4: the registry now carries 100 worksheet entries (57 verified R2
 * assets, 43 in_progress) seeded from the fp-coding-bundle master plan. The
 * ready set combines the existing preflight assets with generated worksheets.
 * The /worksheets route paginates this list client-side since it's a
 * static asset.
 */
export type WorksheetStatus = 'ready' | 'in_progress' | 'not_yet_designed';
export type WorksheetLevel = 'L1' | 'L2' | 'L3' | 'L4';
export type WorksheetBagianNum = 1 | 2 | 3;

export interface WorksheetSummary {
  worksheetId: string;
  bundleNo: number;
  bagian: WorksheetBagianNum;
  level: WorksheetLevel;
  konsep: string;
  judul: string;
  slug: string;
  deskripsi: string;
  skillYangDilatih: string;
  status: WorksheetStatus;
  thumbnailUrl?: string;
  pdfUrl?: string;
  answerKeyUrl?: string;
  /** R2 object key for the printable PDF (e.g. `worksheets/ws-001/pdf`). */
  r2Key?: string;
  /** R2 object key for the worksheet thumbnail. */
  thumbnailR2Key?: string;
  /** Hydrated by catalogStateQuery. Defaults to true when missing. */
  isEnabled?: boolean;
}

/**
 * Resolve an R2 object key to a browser-loadable URL.
 *
 * In dev we point `VITE_R2_PUBLIC_BASE_URL` at the dev R2 bucket
 * (`https://pub-…r2.dev/…`) so the SPA can load thumbnails and PDFs
 * directly without needing the Worker `/api/assets/...` proxy to be
 * running. In production the variable should be set to the
 * worker-fronted public hostname (e.g. `https://assets.terusmengajar.id`)
 * — or simply left unset and the helper will fall back to the same-origin
 * `/api/assets/<key>` route which streams the object from the bound R2
 * bucket.
 */
export function resolveWorksheetAssetUrl(key: string | undefined | null): string | undefined {
  if (!key) return undefined;
  const base = (import.meta.env?.VITE_R2_PUBLIC_BASE_URL as string | undefined) ?? '';
  const trimmedBase = base.replace(/\/+$/, '');
  // If the entry is already an absolute URL, return it untouched.
  if (/^https?:\/\//i.test(key)) return key;
  if (trimmedBase) return `${trimmedBase}/${key.replace(/^\/+/, '')}`;
  // Same-origin Worker proxy default.
  return `/api/assets/${key.replace(/^\/+/, '')}`;
}

/**
 * Same-origin URL of a worksheet's PDF, served by the worker route
 * `/api/assets/worksheets/:id/pdf`. Use this where only the worksheet id is
 * known (progress cards); the catalog's `pdfUrl` resolves to the same route.
 * Never link to a bare worksheets path ending in .pdf — that is a SPA route, not a file.
 */
export function worksheetPdfHref(worksheetId: string): string {
  return `/api/assets/worksheets/${worksheetId}/pdf`;
}

export interface PublicCatalog {
  total: number;
  publicGames: number;
  games: GameSummary[];
  worksheets: WorksheetSummary[];
  totalWorksheets?: number;
  readyWorksheets?: number;
}

export const publicCatalogQuery = queryOptions({
  queryKey: ['catalog', 'public'] as const,
  queryFn: async () => {
    const url = (import.meta.env?.VITE_SITE_URL as string | undefined) ?? '';
    const res = await fetch(`${url}/registry.json`);
    const json = (await res.json()) as {
      totalGames: number;
      publicGames: number;
      games: GameSummary[];
      worksheets?: WorksheetSummary[];
      totalWorksheets?: number;
      readyWorksheets?: number;
    };
    // Pre-resolve PDF / thumbnail URLs so consumers don't need to know about
    // the R2 base hostname. The resolver falls back to the same-origin
    // /api/assets proxy when VITE_R2_PUBLIC_BASE_URL isn't set.
    const worksheets = (json.worksheets ?? []).map((w) => ({
      ...w,
      pdfUrl: resolveWorksheetAssetUrl(w.r2Key) ?? w.pdfUrl,
      thumbnailUrl: resolveWorksheetAssetUrl(w.thumbnailR2Key) ?? w.thumbnailUrl,
    }));
    return {
      total: json.totalGames,
      publicGames: json.publicGames,
      games: json.games,
      worksheets,
      totalWorksheets: json.totalWorksheets ?? json.worksheets?.length ?? 0,
      readyWorksheets: json.readyWorksheets ?? 0,
    };
  },
  staleTime: 5 * 60_000,
});

/**
 * Catalog toggle state — maps every gameId/worksheetId to its current
 * admin/tutor enable flag. Sourced from /api/catalog/state (joins D1
 * game_state + worksheet_state). Items missing from those tables default
 * to enabled=true (handled client-side in `mergeCatalogWithState`).
 */
export interface PublicCatalogState {
  games: Record<string, boolean>;
  worksheets: Record<string, boolean>;
  fetchedAt: string;
}

export const catalogStateQuery = queryOptions({
  queryKey: ['catalog', 'state'] as const,
  queryFn: async () =>
    fetchApi<PublicCatalogState>('/api/catalog/state'),
  staleTime: 30_000,
  retry: 1,
});

/**
 * Merge a static registry catalog (from publicCatalogQuery) with the
 * runtime toggle state (from catalogStateQuery). Returns new array refs
 * so React Query change detection fires.
 *
 * Items missing from the state map default to enabled=true (optimistic).
 */
export function mergeCatalogWithState<
  T extends { gameId?: string; worksheetId?: string; isEnabled?: boolean }
>(
  items: T[],
  stateMap: Record<string, boolean>,
  idField: 'gameId' | 'worksheetId',
): T[] {
  return items.map((item) => ({
    ...item,
    isEnabled: stateMap[item[idField] ?? ''] ?? true,
  }));
}

export function findWorksheetBySlug(
  worksheets: WorksheetSummary[],
  slug: string,
): WorksheetSummary | undefined {
  return worksheets.find((w) => w.slug === slug);
}

/**
 * Worksheet filter spec — used by the /worksheets catalog page so
 * visitors can narrow the 100-item bundle by bagian / level / konsep /
 * status. The client computes filtered + paginated slices; the static
 * registry.json stays the single source of truth.
 */
export interface WorksheetFilters {
  bagian?: WorksheetBagianNum;
  level?: WorksheetLevel;
  konsep?: string;
  status?: WorksheetStatus;
}

export function filterWorksheets(
  worksheets: WorksheetSummary[],
  filters: WorksheetFilters,
): WorksheetSummary[] {
  return worksheets.filter((w) => {
    if (filters.bagian && w.bagian !== filters.bagian) return false;
    if (filters.level && w.level !== filters.level) return false;
    if (filters.konsep && w.konsep !== filters.konsep) return false;
    if (filters.status && w.status !== filters.status) return false;
    return true;
  });
}

/**
 * Default catalog order: materials that can be used now come first, followed
 * by upcoming work. Within each group, learners progress from L1 to L4.
 */
export function sortWorksheetsForCatalog(worksheets: WorksheetSummary[]): WorksheetSummary[] {
  const statusOrder: Record<WorksheetStatus, number> = {
    ready: 0,
    in_progress: 1,
    not_yet_designed: 2,
  };
  const levelOrder: Record<WorksheetLevel, number> = { L1: 1, L2: 2, L3: 3, L4: 4 };

  return [...worksheets].sort(
    (a, b) =>
      statusOrder[a.status] - statusOrder[b.status] ||
      levelOrder[a.level] - levelOrder[b.level] ||
      a.bagian - b.bagian ||
      a.bundleNo - b.bundleNo,
  );
}

export interface PaginatedWorksheets {
  items: WorksheetSummary[];
  total: number;
  page: number;
  pageCount: number;
  pageSize: number;
}

export function paginateWorksheets(
  worksheets: WorksheetSummary[],
  page: number,
  pageSize: number,
): PaginatedWorksheets {
  const safeSize = Math.max(1, Math.floor(pageSize) || 1);
  const pageCount = Math.max(1, Math.ceil(worksheets.length / safeSize));
  const safePage = Math.min(pageCount, Math.max(1, Math.floor(page) || 1));
  const start = (safePage - 1) * safeSize;
  return {
    items: worksheets.slice(start, start + safeSize),
    total: worksheets.length,
    page: safePage,
    pageCount,
    pageSize: safeSize,
  };
}

/**
 * Resolve a single game by slug from the catalog cache. Cheap lookup — the
 * catalog is small (30 games max) and we already have it in memory.
 */
export function findGameBySlug(games: GameSummary[], slug: string): GameSummary | undefined {
  return games.find((g) => g.slug === slug);
}

export interface GameDetailResponse {
  gameId: string;
  title: string;
  message?: string;
}

export function gameDetailQuery(gameId: string) {
  return queryOptions({
    queryKey: ['games', 'detail', gameId] as const,
    queryFn: () => fetchApi<GameDetailResponse>(`/api/games/${gameId}`),
    enabled: Boolean(gameId),
  });
}

export interface GameAccessResponse {
  allowed: boolean;
  gameId: string;
  message?: string;
}

export function gameAccessQuery(gameId: string) {
  return queryOptions({
    queryKey: ['games', 'access', gameId] as const,
    queryFn: () => fetchApi<GameAccessResponse>(`/api/games/${gameId}/access`),
    enabled: Boolean(gameId),
  });
}

// ── Classes ──────────────────────────────────────────────────────────

export interface ClassroomSummary {
  id: string;
  name: string;
  sdGrade: number;
  scheduleText?: string;
  maxStudents: number;
  colorAccent: string;
  zoomUrl?: string | null;
  scratchJrAppUrl?: string | null;
  tutorUserId?: string;
  isActive?: boolean;
}

export interface ClassListResponse {
  classes: ClassroomSummary[];
  total: number;
}

export const classesQuery = queryOptions({
  queryKey: ['classes'] as const,
  queryFn: () => fetchApi<ClassListResponse>('/api/classes'),
  staleTime: 30_000,
});

export interface CreateClassPayload {
  name: string;
  sdGrade: number;
  scheduleText?: string;
  maxStudents?: number;
  colorAccent?: string;
  zoomUrl?: string | null;
  scratchJrAppUrl?: string | null;
}

export function classDetailQuery(classId: string) {
  return queryOptions({
    queryKey: ['classes', 'detail', classId] as const,
    queryFn: () => fetchApi<ClassroomSummary>(`/api/classes/${classId}`),
    enabled: Boolean(classId),
  });
}

// ── Invites ──────────────────────────────────────────────────────────

export interface InviteSummary {
  id: string;
  code: string;
  expiresAt: string;
}

export interface ClassInvitesResponse {
  invites: InviteSummary[];
  classId: string;
}

export function classInvitesQuery(classId: string) {
  return queryOptions({
    queryKey: ['classes', 'invites', classId] as const,
    queryFn: () => fetchApi<ClassInvitesResponse>(`/api/classes/${classId}/invites`),
    enabled: Boolean(classId),
  });
}

// ── Members ──────────────────────────────────────────────────────────

export interface ClassMember {
  userId: string;
  name: string;
  email?: string;
  joinedAt: string;
}

export interface ClassMembersResponse {
  members: ClassMember[];
  classId: string;
}

export function classMembersQuery(classId: string) {
  return queryOptions({
    queryKey: ['classes', 'members', classId] as const,
    queryFn: () => fetchApi<ClassMembersResponse>(`/api/classes/${classId}/members`),
    enabled: Boolean(classId),
  });
}

// ── Access ───────────────────────────────────────────────────────────

export interface ClassGameAccessEntry {
  gameId: string;
  opened: boolean;
  openedAt?: string;
}

export interface ClassGameAccessResponse {
  games: ClassGameAccessEntry[];
  classId: string;
}

export function classGameAccessQuery(classId: string) {
  return queryOptions({
    queryKey: ['classes', 'access', 'games', classId] as const,
    queryFn: () => fetchApi<ClassGameAccessResponse>(`/api/classes/${classId}/access/games`),
    enabled: Boolean(classId),
  });
}

// GAP-04: the class-detail "Game dibuka" metric used to read
// /api/classes/{id}/access/games — an empty-for-classes endpoint — so the
// metric always showed 0 while the Konten kelas tab (this endpoint)
// showed the real count. One shared query key means the metric and the
// tab now read the same cache and refresh together after toggles.
export interface ClassContentGameRow {
  gameId: string;
  slug: string | null;
  title: string;
  sdGrade: number | null;
  levelCount: number;
  visibility: string;
  status: string;
  displayStatus: string | null;
  globallyEnabled: boolean;
  opened: boolean;
}

export interface ClassContentGamesData {
  games: ClassContentGameRow[];
  total: number;
}

export function classContentGamesQuery(classId: string) {
  return queryOptions({
    queryKey: ['class-content', classId, 'games'] as const,
    queryFn: async () => {
      const res = await fetchApi<ClassContentGamesData>(
        `/api/classes/${classId}/games`,
      );
      if (!res.ok) throw new Error('Gagal memuat daftar game.');
      return res.data;
    },
    enabled: Boolean(classId),
  });
}

// ── Me: per-class content (student side) ─────────────────────────────

/**
 * Worksheet the tutor opened for one of the student's classes
 * (GET /api/me/class-content). Drives the "Materi kelas" list and the
 * "Dibuka tutor" catalog badge.
 */
export interface ClassOpenedWorksheet {
  worksheetId: string;
  slug: string;
  judul: string;
  level: string;
  bagian: number;
  konsep: string;
  status: string;
}

export interface ClassOpenedGame {
  gameId: string;
  slug: string;
  title: string;
  status: string;
}

export interface ClassContentEntry {
  classId: string;
  className: string;
  worksheets: ClassOpenedWorksheet[];
  games: ClassOpenedGame[];
}

export interface MyClassContentData {
  classes: ClassContentEntry[];
}

export const myClassContentQuery = queryOptions({
  queryKey: ['me', 'class-content'] as const,
  queryFn: async () => {
    const res = await fetchApi<MyClassContentData>('/api/me/class-content');
    if (!res.ok) throw new Error('Gagal memuat materi kelas.');
    return res.data;
  },
  // Callers gate this with `enabled: isLoggedIn` — guests get 401 from
  // /api/me/* so the query should not run for them.
});

// ── Progress ──────────────────────────────────────────────────────────

/**
 * Per-game progress row. Drives <GameProgressCard>.
 */
export interface GameProgress {
  gameId: string;
  gameSlug: string;
  title: string;
  grade: number;
  totalLevels: number;
  completedLevels: number;
  starsEarned: number;
  lastPlayedAt: string | null;
}

/**
 * Per-worksheet progress row. Drives <WorksheetProgressCard>.
 */
export interface WorksheetProgress {
  worksheetId: string;
  title: string;
  completed: boolean;
  completedAt: string | null;
}

/**
 * Recent activity feed entry. Drives <RecentActivityFeed>.
 */
export interface ProgressEvent {
  id: string;
  kind: 'level_completed' | 'worksheet_completed' | 'badge_earned' | 'class_joined' | 'quiz_stars';
  label: string;
  detail?: string;
  occurredAt: string;
}

/**
 * Aggregate numbers for <ProgressHeader>.
 */
export interface ProgressAggregate {
  gamesStarted: number;
  gamesCompleted: number;
  gameStars: number;
  quizStars: number;
  totalStars: number;
  quizzesCompleted: number;
  worksheetsCompleted: number;
  totalWorksheets: number;
  streakDays: number;
  badgesEarned: number;
}

export interface ProgressSummary {
  aggregate: ProgressAggregate;
  games: GameProgress[];
  worksheets: WorksheetProgress[];
  recentActivity: ProgressEvent[];
  /** Last stored learning pulse per active class; membership is separate. */
  classActivity?: ClassLearningActivity[];
}

export interface ClassLearningActivity {
  classId: string;
  lastActivityAt: string | null;
}

export const myProgressQuery = queryOptions({
  queryKey: ['progress', 'me'] as const,
  queryFn: () => fetchApi<ProgressSummary>('/api/progress/me'),
  staleTime: 30_000,
});

// ── Admin ────────────────────────────────────────────────────────────

export interface PendingTutor {
  userId: string;
  name: string;
  email: string;
  displayName?: string | null;
  registeredAt: string;
}

export interface PendingTutorsResponse {
  tutors: PendingTutor[];
  total: number;
}

export const pendingTutorsQuery = queryOptions({
  queryKey: ['admin', 'tutors', 'pending'] as const,
  queryFn: () => fetchApi<PendingTutorsResponse>('/api/admin/tutors/pending'),
  staleTime: 60_000,
});

// ── Admin Tutor CRUD (N-08) ───────────────────────────────────────

export interface TutorRow {
  userId: string;
  email: string;
  name: string;
  displayName: string | null;
  isActive: boolean;
  approvedAt: string | null;
  approvedByUserId: string | null;
  registeredAt: string;
}

export interface TutorsResponse {
  tutors: TutorRow[];
  total: number;
}

export interface TutorsListParams {
  active?: boolean;
  search?: string;
}

export function tutorsListQuery(params: TutorsListParams = {}) {
  const search = new URLSearchParams();
  if (params.active !== undefined) search.set('active', String(params.active));
  if (params.search) search.set('search', params.search);
  const qs = search.toString();
  return queryOptions({
    queryKey: ['admin', 'tutors', 'list', params] as const,
    queryFn: () => fetchApi<TutorsResponse>(`/api/admin/tutors${qs ? `?${qs}` : ''}`),
    staleTime: 60_000,
  });
}

export interface AuditEntry {
  id: string;
  actorUserId: string | null;
  actorRole: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AuditPage {
  entries: AuditEntry[];
  total: number;
  page: number;
  limit: number;
}

export function auditQuery(page: number, limit = 20) {
  return queryOptions({
    queryKey: ['admin', 'audit', page, limit] as const,
    queryFn: () => fetchApi<AuditPage>(`/api/admin/audit?page=${page}&limit=${limit}`),
  });
}

export interface SyncStatus {
  lastSync: string | null;
  status: 'idle' | 'running' | 'success' | 'failed' | string;
  lastRun: string | null;
  lastSuccess: string | null;
}

export const syncStatusQuery = queryOptions({
  queryKey: ['admin', 'sync', 'status'] as const,
  queryFn: () => fetchApi<SyncStatus>('/api/admin/sync/status'),
  staleTime: 15_000,
});

export interface SyncRun {
  id: string;
  status: string;
  last_run: string;
  last_success: string | null;
}

export const syncRunsQuery = queryOptions({
  queryKey: ['admin', 'sync', 'runs'] as const,
  queryFn: () => fetchApi<{ runs: SyncRun[]; total: number }>('/api/admin/sync/runs'),
  staleTime: 30_000,
});

// ── Admin Siswa List (N-09) ─────────────────────────────────────

export interface SiswaRow {
  userId: string;
  email: string;
  name: string;
  isActive: boolean;
  registeredAt: string;
  classCount: number;
  gamesCompletedCount: number;
  worksheetsCompletedCount: number;
}

export interface SiswaListResponse {
  students: SiswaRow[];
  total: number;
  page: number;
  limit: number;
}

export type SiswaStatusFilter = 'all' | 'active' | 'removed';

export interface SiswaListParams {
  classId?: string;
  status?: SiswaStatusFilter;
  search?: string;
  page?: number;
  limit?: number;
}

export function siswaListQuery(params: SiswaListParams = {}) {
  const search = new URLSearchParams();
  if (params.classId) search.set('classId', params.classId);
  if (params.status && params.status !== 'all') search.set('status', params.status);
  if (params.search) search.set('search', params.search);
  if (params.page) search.set('page', String(params.page));
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  return queryOptions({
    queryKey: ['admin', 'students', 'list', params] as const,
    queryFn: () => fetchApi<SiswaListResponse>(`/api/admin/students${qs ? `?${qs}` : ''}`),
    staleTime: 60_000,
  });
}

export interface SiswaMembership {
  classId: string;
  className: string;
  sdGrade: number;
  scheduleText: string | null;
  joinedAt: string;
}

export interface SiswaProgressSummary {
  gamesStarted: number;
  gamesCompleted: number;
  worksheetsCompleted: number;
  totalStars: number;
}

export interface SiswaActivityEvent {
  id: string;
  kind: string;
  scopeId: string;
  gameId: string | null;
  worksheetId: string | null;
  completed: boolean;
  occurredAt: string;
}

export interface SiswaDetailResponse {
  profile: {
    userId: string;
    email: string;
    name: string;
    isActive: boolean;
    registeredAt: string;
  };
  memberships: SiswaMembership[];
  progressSummary: SiswaProgressSummary;
  recentActivity: SiswaActivityEvent[];
}

export function siswaDetailQuery(userId: string) {
  return queryOptions({
    queryKey: ['admin', 'students', 'detail', userId] as const,
    queryFn: () => fetchApi<SiswaDetailResponse>(`/api/admin/students/${userId}`),
    enabled: Boolean(userId),
  });
}

// ── Preferences ─────────────────────────────────────────────────────

export interface UserPreferences {
  focusMode: 'standard' | 'focus';
  reducedMotion: boolean;
  muted: boolean;
  language: 'id';
}

// ── Lesson plans (Task 23) ─────────────────────────────────────

export type LessonItemSource = 'game' | 'worksheet';

export interface LessonPlanItem {
  id: string;
  planId: string;
  position: number;
  source: LessonItemSource;
  gameId: string | null;
  worksheetId: string | null;
  durationMin: number;
  note: string;
  createdAt: string;
}

export interface LessonPlan {
  id: string;
  ownerUserId: string;
  title: string;
  description: string;
  targetClassId: string | null;
  durationMin: number;
  createdAt: string;
  updatedAt: string;
  items: LessonPlanItem[];
  worksheetCount: number;
  gameCount: number;
  estimatedPrintMin: number;
}

export interface LessonPlanList {
  plans: Array<
    Omit<LessonPlan, 'items'> & { itemCount: number }
  >;
  total: number;
}

export const lessonPlanListQuery = queryOptions({
  queryKey: ['lesson-plans'] as const,
  queryFn: () => fetchApi<LessonPlanList>('/api/lesson-plans'),
  staleTime: 60_000,
});

export const lessonPlanQuery = (planId: string) =>
  queryOptions({
    queryKey: ['lesson-plan', planId] as const,
    queryFn: () => fetchApi<LessonPlan>(`/api/lesson-plans/${planId}`),
    enabled: !!planId,
    staleTime: 60_000,
  });

// ── Tutor aggregated student roster (N-27) ────────────────────────

export interface TutorStudentClassMembership {
  classId: string;
  className: string;
  sdGrade: number | null;
  joinedAt: string;
}

export interface TutorStudentRow {
  studentId: string;
  name: string;
  email: string | null;
  classes: TutorStudentClassMembership[];
  gamesCompleted: number;
  gamesAssigned: number;
  worksheetsCompleted: number;
  worksheetsAssigned: number;
  totalStars: number;
  totalHints: number;
  completionRate: number;
  lastActiveAt: string | null;
  isActive: boolean;
}

export interface TutorStudentClassOption {
  classId: string;
  className: string;
  sdGrade: number | null;
  studentCount: number;
}

export interface TutorStudentsResponse {
  students: TutorStudentRow[];
  classes: TutorStudentClassOption[];
  totalCount: number;
  tutorHasClasses: boolean;
}

export interface TutorStudentPerGame {
  gameId: string;
  title: string;
  completedLevels: number;
  totalLevels: number;
  starsEarned: number;
  hintsUsed: number;
  lastPlayedAt: string | null;
}

export interface TutorStudentPerWorksheet {
  worksheetId: string;
  title: string;
  completed: boolean;
  completedAt: string | null;
  timeSpentSec: number;
}

export interface TutorStudentActivityEntry {
  id: string;
  kind: string;
  label: string;
  detail: string | null;
  occurredAt: string;
}

export interface TutorStudentDetail {
  student: TutorStudentRow;
  perGame: TutorStudentPerGame[];
  perWorksheet: TutorStudentPerWorksheet[];
  recentActivity: TutorStudentActivityEntry[];
}

export const tutorStudentsQuery = (params: { q?: string; classId?: string; status?: string } = {}) =>
  queryOptions({
    queryKey: ['tutor-students', params] as const,
    queryFn: () => {
      const search = new URLSearchParams();
      if (params.q) search.set('q', params.q);
      if (params.classId) search.set('classId', params.classId);
      if (params.status) search.set('status', params.status);
      const qs = search.toString();
      return fetchApi<TutorStudentsResponse>(
        `/api/tutor/students${qs ? `?${qs}` : ''}`,
      );
    },
    staleTime: 30_000,
  });

export const tutorStudentDetailQuery = (studentId: string) =>
  queryOptions({
    queryKey: ['tutor-student', studentId] as const,
    queryFn: () =>
      fetchApi<TutorStudentDetail>(`/api/tutor/students/${studentId}`),
    enabled: !!studentId,
    staleTime: 30_000,
  });

// ── Badges (N-06) ────────────────────────────────────────────────

export interface BadgeEntry {
  badgeId: string;
  title: string;
  description: string;
  ruleType:
    | 'games_completed'
    | 'levels_completed'
    | 'worksheets_completed'
    | 'stars_earned'
    | 'streak_days'
    | 'custom';
  threshold: number;
  awardedAt: string | null;
  currentValue?: number;
}

export interface BadgesResponse {
  earned: BadgeEntry[];
  attainableSoon: BadgeEntry[];
  locked: BadgeEntry[];
}

export const myBadgesQuery = (params: { userId?: string; scopeId?: string } = {}) =>
  queryOptions({
    queryKey: ['badges', params] as const,
    queryFn: () => {
      const search = new URLSearchParams();
      if (params.userId) search.set('userId', params.userId);
      if (params.scopeId) search.set('scopeId', params.scopeId);
      const qs = search.toString();
      return fetchApi<BadgesResponse>(`/api/me/badges${qs ? `?${qs}` : ''}`);
    },
    staleTime: 30_000,
  });

export const preferencesQuery = queryOptions({
  queryKey: ['me', 'preferences'] as const,
  queryFn: () =>
    fetchApi<UserPreferences>('/api/me/preferences').then((res) => {
      if (res.ok) return res;
      // Backend has no data yet — fall back to defaults.
      return {
        ok: true as const,
        data: {
          focusMode: 'standard' as const,
          reducedMotion: false,
          muted: false,
          language: 'id' as const,
        },
      } satisfies ApiResponse<UserPreferences>;
    }),
  staleTime: 300_000,
});

// ── Per-class progress (N-14 tutor dashboard) ────────────────────

export interface ClassProgressTopPerformer {
  studentId: string;
  name: string;
  totalStars: number;
}

export interface ClassProgressStruggling {
  studentId: string;
  name: string;
  completionRate: number;
}

export interface ClassProgressRecentActivity {
  id: string;
  studentId: string;
  studentName: string;
  kind: string;
  label: string;
  detail: string | null;
  occurredAt: string;
}

export interface ClassProgressSummary {
  totalStudents: number;
  activeStudentsThisWeek: number;
  averageCompletion: number;
  topPerformer: ClassProgressTopPerformer | null;
  strugglingStudents: ClassProgressStruggling[];
  recentActivity: ClassProgressRecentActivity[];
}

export interface ClassProgressStudentRow {
  studentId: string;
  name: string;
  email: string | null;
  joinedAt: string;
  lastActiveAt: string | null;
  gamesCompleted: number;
  gamesAssigned: number;
  worksheetsCompleted: number;
  worksheetsAssigned: number;
  totalStars: number;
  totalHints: number;
  completionRate: number;
}

export interface ClassProgressStudentsList {
  students: ClassProgressStudentRow[];
}

export interface ClassProgressStudentGameRow {
  gameId: string;
  title: string;
  completedLevels: number;
  totalLevels: number;
  starsEarned: number;
  hintsUsed: number;
  lastPlayedAt: string | null;
}

export interface ClassProgressStudentWorksheetRow {
  worksheetId: string;
  title: string;
  completed: boolean;
  completedAt: string | null;
  timeSpentSec: number;
}

export interface ClassProgressStudentDetail {
  student: ClassProgressStudentRow;
  perGame: ClassProgressStudentGameRow[];
  perWorksheet: ClassProgressStudentWorksheetRow[];
}

export const classProgressSummaryQuery = (classId: string) =>
  queryOptions({
    queryKey: ['class-progress', 'summary', classId] as const,
    queryFn: () =>
      fetchApi<ClassProgressSummary>(`/api/classes/${classId}/progress/summary`),
    enabled: Boolean(classId),
    staleTime: 30_000,
  });

export const classProgressStudentsQuery = (classId: string) =>
  queryOptions({
    queryKey: ['class-progress', 'students', classId] as const,
    queryFn: () =>
      fetchApi<ClassProgressStudentsList>(
        `/api/classes/${classId}/progress/students`,
      ),
    enabled: Boolean(classId),
    staleTime: 30_000,
  });

export const classProgressStudentDetailQuery = (
  classId: string,
  userId: string,
) =>
  queryOptions({
    queryKey: ['class-progress', 'student', classId, userId] as const,
    queryFn: () =>
      fetchApi<ClassProgressStudentDetail>(
        `/api/classes/${classId}/progress/students/${userId}`,
      ),
    enabled: Boolean(classId) && Boolean(userId),
    staleTime: 30_000,
  });

// ── Parent self-service (N-16) ─────────────────────────────

export interface ParentAccessLinkStatus {
  parent: { id: string; name: string; email: string } | null;
  tokenStatus: 'ok';
  expiresAt: string;
}

export interface ParentAccessLinkStatusError {
  ok: false;
  error: { code: string; message: string };
}

export const parentAccessLinkStatusQuery = (token: string) =>
  queryOptions({
    queryKey: ['parent', 'access-link-status', token] as const,
    queryFn: () =>
      fetchApi<ParentAccessLinkStatus>(
        `/api/parents/me/access-link-status?token=${encodeURIComponent(token)}`,
      ),
    enabled: Boolean(token),
    staleTime: 0,
    retry: false,
  });

// ── Parent dashboard (N-17) ────────────────────────────────

export interface ParentChildSummary {
  studentId: string;
  name: string;
  email: string | null;
  sdGrade: number | null;
  className: string | null;
  joinedAt: string;
}

export interface ParentChildrenList {
  children: ParentChildSummary[];
}

export const myChildrenQuery = queryOptions({
  queryKey: ['parent', 'children'] as const,
  queryFn: () => fetchApi<ParentChildrenList>('/api/me/children'),
  staleTime: 60_000,
});

export const childProgressQuery = (studentId: string) =>
  queryOptions({
    queryKey: ['parent', 'child-progress', studentId] as const,
    queryFn: () => fetchApi<ProgressSummary>(`/api/me/children/${studentId}`),
    enabled: Boolean(studentId),
    staleTime: 30_000,
  });

// ── Tutor reports (N-18) ───────────────────────────────

export interface EligibleReportStudent {
  studentId: string;
  studentName: string;
  studentEmail: string | null;
  parentId: string;
  parentName: string;
  parentEmail: string;
  parentPhone: string | null;
}

export interface EligibleReportStudents {
  students: EligibleReportStudent[];
}

export interface SentReport {
  reportId: string;
  studentId: string;
  /** Audit F19: resolved server-side so the history table is readable. */
  studentName?: string | null;
  parentId: string;
  classId: string | null;
  subject: string;
  body: string;
  deliveryStatus: 'sent' | 'email_only' | 'failed' | 'partial';
  emailMessageId: string | null;
  sentAt: string;
}

export interface SentReportsList {
  reports: SentReport[];
}

export const eligibleStudentsForReportQuery = (classId: string) =>
  queryOptions({
    queryKey: ['reports', 'eligible-students', classId] as const,
    queryFn: () =>
      fetchApi<EligibleReportStudents>(
        `/api/classes/${classId}/students-for-report`,
      ),
    enabled: Boolean(classId),
    staleTime: 60_000,
  });

export const sentReportsQuery = queryOptions({
  queryKey: ['reports', 'sent'] as const,
  queryFn: () => fetchApi<SentReportsList>('/api/reports/sent'),
  staleTime: 30_000,
});

// Re-export the helper for any module that prefers the raw wrapper.
export { fetchApi } from './client.js';
